const OVERLAY_DATA_ATTR = "data-palazzo-embedded-overlay"

export type EmbeddedPresentation = "fullscreen" | "panel"

export type PanelCorner = "bottom-right" | "bottom-left"

export interface IframeOptions {
  presentation?: EmbeddedPresentation
  corner?: PanelCorner
}

export const PANEL_CARD_MIN_VIEWPORT_WIDTH = 1100
const PANEL_WIDTH = 720
const PANEL_MAX_HEIGHT = 1440
const PANEL_MARGIN = 24
const SHEET_TOP_GAP = 24

export type PanelVariant = "card" | "sheet"

export function resolvePresentation(
  requested: EmbeddedPresentation,
): EmbeddedPresentation {
  return requested === "panel" ? "panel" : "fullscreen"
}

export function resolvePanelVariant(viewportWidth: number): PanelVariant {
  return viewportWidth >= PANEL_CARD_MIN_VIEWPORT_WIDTH ? "card" : "sheet"
}

export function panelIframeStyle(
  corner: PanelCorner,
  variant: PanelVariant = "card",
): string {
  const shared = [
    "position:fixed",
    "border:0",
    "display:block",
    "background:#fff",
    "box-shadow:0 18px 48px rgba(0,0,0,0.24),0 2px 8px rgba(0,0,0,0.12)",
    "pointer-events:auto",
  ]
  if (variant === "sheet") {
    return shared
      .concat([
        "left:0",
        "right:0",
        "bottom:0",
        "width:100vw",
        `height:min(${PANEL_MAX_HEIGHT}px, calc(100vh - ${SHEET_TOP_GAP}px))`,
        "border-radius:16px 16px 0 0",
      ])
      .join(";")
  }
  return shared
    .concat([
      `bottom:${PANEL_MARGIN}px`,
      corner === "bottom-left"
        ? `left:${PANEL_MARGIN}px`
        : `right:${PANEL_MARGIN}px`,
      `width:min(${PANEL_WIDTH}px, calc(100vw - ${PANEL_MARGIN * 2}px))`,
      `height:min(${PANEL_MAX_HEIGHT}px, calc(100vh - ${PANEL_MARGIN * 2}px))`,
      "border-radius:16px",
    ])
    .join(";")
}

export function createIframe(
  src: string,
  containerId?: string,
  options?: IframeOptions,
): { iframe: HTMLIFrameElement; overlay: HTMLDivElement | null } {
  const container = containerId ? document.getElementById(containerId) : null
  let overlay: HTMLDivElement | null = null
  let target: HTMLElement

  const viewportWidth = typeof window === "undefined" ? 0 : window.innerWidth
  const presentation = resolvePresentation(options?.presentation ?? "fullscreen")

  if (container) {
    target = container
  } else {
    overlay = createOverlay(presentation)
    target = overlay
  }

  const iframe = document.createElement("iframe")
  iframe.src = src
  iframe.style.cssText =
    !container && presentation === "panel"
      ? panelIframeStyle(
          options?.corner ?? "bottom-right",
          resolvePanelVariant(viewportWidth),
        )
      : "width:100%;height:100%;border:0;display:block;"
  iframe.setAttribute("allow", "fullscreen")
  iframe.setAttribute("allowfullscreen", "true")
  target.appendChild(iframe)
  return { iframe, overlay }
}

function createOverlay(presentation: EmbeddedPresentation): HTMLDivElement {
  const div = document.createElement("div")
  div.setAttribute(OVERLAY_DATA_ATTR, "")
  const style = ["position:fixed", "inset:0", "z-index:2147483647"]
  if (presentation === "panel") {
    style.push("background:transparent", "pointer-events:none")
  } else {
    style.push("background:rgba(0,0,0,0.85)")
  }
  div.style.cssText = style.join(";")
  document.body.appendChild(div)
  return div
}

export function removeIframe(
  iframe: HTMLIFrameElement,
  overlay: HTMLDivElement | null,
) {
  iframe.remove()
  if (overlay) overlay.remove()
}
